import React, { useState } from 'react';
import { Loader } from 'lucide-react';
import api from '../../services/api';
import { notifyError, notifySuccess } from '../../utils/notificationCenter';
import {
  ApartmentUnitsEditor,
  ApartmentUnitRow,
  createEmptyApartmentUnit,
  serializeApartmentUnits,
  validateApartmentUnits,
} from './ApartmentUnitsEditor';

type CreateUserRole = 'owner' | 'manager' | 'admin' | 'builder';

interface ResidentialComplex {
  id: string;
  name: string;
}

interface CreateOwnerUserModalProps {
  complexes: ResidentialComplex[];
  onClose: () => void;
  onCreated: () => void;
}

const initialForm = {
  role: 'owner' as CreateUserRole,
  complexId: '',
  assignedComplexIds: [] as string[],
  email: '',
  firstName: '',
  lastName: '',
  phone: '',
  document: '',
  sendWelcomeEmail: true,
  useDocumentAsCredentials: false,
  units: [createEmptyApartmentUnit()] as ApartmentUnitRow[],
};

const roleDescriptions: Record<CreateUserRole, string> = {
  owner: 'Propietario con apartamento y coeficiente. Mismas validaciones que la importación por Excel.',
  manager: 'Gestiona delegaciones de un conjunto asignado. Debe tener conjunto administrado.',
  builder: 'Constructora: auditoría y descarga de poderes firmados. Puede asignarse a varios conjuntos.',
  admin: 'Acceso completo al panel de administración.',
};

export const CreateOwnerUserModal: React.FC<CreateOwnerUserModalProps> = ({
  complexes,
  onClose,
  onCreated,
}) => {
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);

  const handleRoleChange = (role: CreateUserRole) => {
    setForm({
      ...initialForm,
      role,
      sendWelcomeEmail: form.sendWelcomeEmail,
      units: [createEmptyApartmentUnit()],
    });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!form.email || !form.firstName) {
      notifyError('Completa los campos obligatorios: correo y nombre');
      return;
    }

    if (form.role === 'owner') {
      if (!form.complexId) {
        notifyError('Selecciona el conjunto para el propietario');
        return;
      }
      const unitsError = validateApartmentUnits(form.units);
      if (unitsError) {
        notifyError(unitsError);
        return;
      }
    }

    if (form.role === 'manager') {
      if (!form.complexId) {
        notifyError('Selecciona el conjunto administrado para el manager');
        return;
      }
      if (!form.lastName.trim()) {
        notifyError('El apellido es obligatorio para usuarios manager');
        return;
      }
    }

    if (form.role === 'admin' && !form.lastName.trim()) {
      notifyError('El apellido es obligatorio para usuarios admin');
      return;
    }

    if (form.role === 'builder') {
      if (form.assignedComplexIds.length === 0) {
        notifyError('Selecciona al menos un conjunto para la constructora');
        return;
      }
      if (!form.lastName.trim()) {
        notifyError('El apellido es obligatorio para usuarios constructora');
        return;
      }
    }

    setSaving(true);
    try {
      let response;

      if (form.role === 'owner') {
        const apartments = serializeApartmentUnits(form.units);
        response = await api.api.post('/users/create-owner', {
          complexId: form.complexId,
          email: form.email.trim(),
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim() || undefined,
          apartments,
          phone: form.phone.trim() || undefined,
          document: form.document.trim() || undefined,
          sendWelcomeEmail: form.sendWelcomeEmail,
          useDocumentAsCredentials: form.useDocumentAsCredentials,
        });
      } else {
        response = await api.api.post('/users', {
          email: form.email.trim(),
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          role: form.role,
          phone: form.phone.trim() || undefined,
          managedComplexId: form.role === 'manager' ? form.complexId : undefined,
          assignedComplexIds: form.role === 'builder' ? form.assignedComplexIds : undefined,
          sendWelcomeEmail: form.sendWelcomeEmail,
        });
      }

      if (response.data?.success === false) {
        notifyError(response.data?.message || 'No se pudo crear el usuario');
        return;
      }

      const transfers = response.data?.transfers as
        | Array<{ apartment: string; fromEmail: string }>
        | undefined;
      if (transfers && transfers.length > 0) {
        notifySuccess(
          `Usuario creado. ${transfers.length} unidad${transfers.length === 1 ? '' : 'es'} reasignada${transfers.length === 1 ? '' : 's'} desde otros propietarios.`
        );
      } else {
        notifySuccess(response.data?.message || 'Usuario creado exitosamente');
      }
      onCreated();
      onClose();
    } catch (err: any) {
      notifyError(err.response?.data?.message || 'Error al crear el usuario');
    } finally {
      setSaving(false);
    }
  };

  const isOwner = form.role === 'owner';
  const isManager = form.role === 'manager';
  const isBuilder = form.role === 'builder';

  const toggleAssignedComplex = (complexId: string) => {
    setForm((current) => {
      const exists = current.assignedComplexIds.includes(complexId);
      return {
        ...current,
        assignedComplexIds: exists
          ? current.assignedComplexIds.filter((id) => id !== complexId)
          : [...current.assignedComplexIds, complexId],
      };
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white shadow-2xl">
        <div className="border-b border-gray-200 px-6 py-4">
          <h3 className="text-lg font-semibold text-gray-900">Crear usuario</h3>
          <p className="mt-1 text-sm text-gray-600">{roleDescriptions[form.role]}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 px-6 py-5">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700">Rol *</label>
            <select
              value={form.role}
              onChange={(e) => handleRoleChange(e.target.value as CreateUserRole)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
            >
              <option value="owner">Propietario</option>
              <option value="manager">Manager</option>
              <option value="builder">Constructora</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          {(isOwner || isManager) && (
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">
                {isManager ? 'Conjunto administrado *' : 'Conjunto *'}
              </label>
              <select
                value={form.complexId}
                onChange={(e) => setForm({ ...form, complexId: e.target.value })}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Seleccionar conjunto</option>
                {complexes.map((complex) => (
                  <option key={complex.id} value={complex.id}>
                    {complex.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {isBuilder && (
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">
                Conjuntos asignados *
              </label>
              <div className="max-h-40 space-y-1 overflow-y-auto rounded-md border border-gray-300 p-2">
                {complexes.map((complex) => (
                  <label key={complex.id} className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={form.assignedComplexIds.includes(complex.id)}
                      onChange={() => toggleAssignedComplex(complex.id)}
                      className="rounded border-gray-300 text-blue-600"
                    />
                    {complex.name}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">Correo *</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                required
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">Teléfono</label>
              <input
                type="text"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">Nombre *</label>
              <input
                type="text"
                value={form.firstName}
                onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                required
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">
                Apellido{isOwner ? '' : ' *'}
              </label>
              <input
                type="text"
                value={form.lastName}
                onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                required={!isOwner}
              />
            </div>
          </div>

          {isOwner && (
            <>
              <ApartmentUnitsEditor
                units={form.units}
                onChange={(units) => setForm({ ...form, units })}
                showTower
              />

              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700">Documento</label>
                <input
                  type="text"
                  value={form.document}
                  onChange={(e) => setForm({ ...form, document: e.target.value })}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
            </>
          )}

          <div className="space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.sendWelcomeEmail}
                onChange={(e) => setForm({ ...form, sendWelcomeEmail: e.target.checked })}
                className="rounded border-gray-300 text-blue-600"
              />
              Enviar email de bienvenida con credenciales
            </label>
            {isOwner && (
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={form.useDocumentAsCredentials}
                  onChange={(e) => setForm({ ...form, useDocumentAsCredentials: e.target.checked })}
                  className="rounded border-gray-300 text-blue-600"
                />
                Usar documento como contraseña (solo usuarios nuevos)
              </label>
            )}
          </div>

          <div className="flex justify-end gap-3 border-t border-gray-200 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? <Loader className="h-4 w-4 animate-spin" /> : null}
              <span>{saving ? 'Creando...' : 'Crear usuario'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
